import { Badge, ServerTable, SkeletonSpinner, Text } from '@civilgrid/ui/components';
import { buildSearchPayload, useTableSettings, useTableStoreState } from '@civilgrid/ui/hooks';
import { createFilterGroup, createFilterLine } from '@civilgrid/ui/utils';
import { useQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { listFilteredAgencies } from '~/api/civilsearch';
import { MergeAgenciesDialog } from './bulkActions/MergeAgenciesDialog';
import { useBulkActions } from './bulkActions/useBulkActions';
import { columns, getAgencyRowLabel } from './columns';

const TABLE_ID = 'agencies-v2';

export default function Agencies() {
  const headingId = useId();
  const { initialPageSize } = useTableSettings({ id: TABLE_ID });

  const stateControl = useTableStoreState({
    defaultPageSize: initialPageSize ?? 100,
    initialSorting: [{ id: 'agencies.agencyId', desc: false }],
    // Merged-away agencies are hidden by the default Merged filter; remove or flip the line to see them.
    initialFilters: createFilterGroup([createFilterLine('agencies.merged', 'eq', 'false')]),
    // Saved filter state wins over initialFilters, so the storage key is versioned with the default.
    storageKey: 'cs-agencies-v2',
  });

  const { pagination, sorting, filters, globalFilter } = stateControl;

  const query = useQuery({
    gcTime: 0,
    refetchOnWindowFocus: false,
    queryKey: ['agencies', { pagination, sorting, filters, globalFilter }],
    queryFn: async () => {
      const searchPayload = buildSearchPayload({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sorting,
        filters,
        globalFilter,
      });
      const apiPayload = {
        ...searchPayload,
        sort: searchPayload.sort?.[0],
      };
      const res = await listFilteredAgencies({
        search: apiPayload,
      });
      return res;
    },
  });

  const agencies = query.data?.agencies ?? [];

  // Bumped after a successful bulk action to clear the table's row selection.
  const [selectionResetKey, setSelectionResetKey] = useState(0);

  const { merge, bulkActions } = useBulkActions({
    agencies,
    onMutationSuccess: () => {
      query.refetch();
      setSelectionResetKey((v) => v + 1);
    },
  });

  if (query.isError) {
    return <div>Failed to fetch agencies.</div>;
  }

  return (
    <div className="flex h-screen-height-minus-top-nav flex-col">
      <div className="flex items-center gap-3 mb-3 mt-2">
        <Text id={headingId} className="text-primary text-xl font-semibold">
          Agencies
        </Text>
        <Badge kind="count">
          {query.isLoading ? <SkeletonSpinner size={18} /> : (query.data?.totalDocuments.toLocaleString() ?? 0)}
        </Badge>
      </div>
      <div className="min-h-0 flex-1">
        <ServerTable
          id={TABLE_ID}
          aria-labelledby={headingId}
          columns={columns}
          data={{
            data: agencies,
            rowCount: query.data?.totalDocuments ?? 0,
            isLoading: query.isLoading,
            isFetching: query.isFetching,
            error: query.error || null,
          }}
          stateControl={stateControl}
          enableColumnGrouping={false}
          isLoading={query.isLoading}
          // CG-614: selection only exists to feed bulk actions, so hide the
          // checkbox column when the flag leaves no actions to take.
          enableRowSelection={bulkActions.length > 0}
          bulkActions={bulkActions}
          getRowLabel={getAgencyRowLabel}
          selectionResetKey={selectionResetKey}
          className="h-full"
        />
      </div>
      <MergeAgenciesDialog
        open={merge.open}
        agencies={merge.selectedAgencies}
        onClose={merge.close}
        onSubmit={merge.onSubmit}
        isPending={merge.isPending}
      />
    </div>
  );
}
